import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { WorkshopDto } from '@workshop-hst/shared';
import { HostCrmPanel } from '../components/HostCrmPanel';
import { SearchBox } from '../components/SearchBox';
import { StatusPill } from '../components/StatusPill';
import { api, ApiError, clearAdminKey, getAdminKey, setAdminKey } from '../lib/api';
import { formatDateTime } from '../lib/format';

export function AdminCrmPage() {
  const [hasKey, setHasKey] = useState(() => Boolean(getAdminKey()));
  if (!hasKey) return <AdminCrmKeyPrompt onSet={() => setHasKey(true)} />;
  return <AdminCrmWorkspace onSignOut={() => setHasKey(false)} />;
}

function AdminCrmKeyPrompt({ onSet }: { onSet: () => void }) {
  const [key, setKey] = useState('');
  return (
    <div className="container-page max-w-md py-24">
      <h1 className="text-2xl font-semibold tracking-tight">Admin sign in</h1>
      <p className="mt-2 text-sm text-ink-muted">Enter your password to continue.</p>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setAdminKey(key.trim());
          onSet();
        }}
      >
        <input
          type="password"
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className="input"
          placeholder="Password"
        />
        <button type="submit" className="btn-primary w-full" disabled={key.trim().length === 0}>
          Continue
        </button>
      </form>
    </div>
  );
}

function AdminCrmWorkspace({ onSignOut }: { onSignOut: () => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['admin', 'workshops'],
    queryFn: () => api.admin.listWorkshops(),
    retry: false,
  });

  const workshops = query.data?.workshops ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return workshops.filter((w) => {
      if (!q) return true;
      return (
        w.title.toLowerCase().includes(q) ||
        w.hostName.toLowerCase().includes(q) ||
        (w.guest?.name.toLowerCase().includes(q) ?? false) ||
        (w.guest?.companyName.toLowerCase().includes(q) ?? false) ||
        w.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    });
  }, [search, workshops]);

  const selectedId = searchParams.get('workshop') ?? filtered[0]?.id ?? workshops[0]?.id ?? null;
  const selected = workshops.find((w) => w.id === selectedId) ?? filtered[0] ?? workshops[0] ?? null;

  if (query.isError) {
    const msg = query.error instanceof ApiError && query.error.status === 401
      ? 'Admin key was rejected. Sign out and try again.'
      : 'Failed to load admin workshops.';
    return (
      <div className="container-page py-16">
        <p className="text-sm text-red-600">{msg}</p>
        <button
          className="btn-secondary mt-4"
          onClick={() => {
            clearAdminKey();
            onSignOut();
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="container-page py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-accent">Admin</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">Host CRM</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            One sequential host workflow per workshop, separate from workshop editing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin" className="btn-secondary">
            Workshops
          </Link>
          {selected ? (
            <Link to={`/admin/workshops/${selected.id}`} className="btn-secondary">
              Edit workshop
            </Link>
          ) : null}
          <button
            className="text-sm text-ink-muted hover:text-ink"
            onClick={() => {
              clearAdminKey();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[340px_1fr]">
        <aside className="rounded-2xl border border-line bg-surface p-4 shadow-card">
          <SearchBox value={search} onChange={setSearch} />
          <div className="mt-4 max-h-[72vh] space-y-2 overflow-y-auto pr-1">
            {query.isLoading ? (
              <div className="space-y-2">
                <div className="h-20 animate-pulse rounded-xl bg-canvas" />
                <div className="h-20 animate-pulse rounded-xl bg-canvas" />
                <div className="h-20 animate-pulse rounded-xl bg-canvas" />
              </div>
            ) : filtered.length > 0 ? (
              filtered.map((w) => (
                <WorkshopCrmButton
                  key={w.id}
                  workshop={w}
                  active={selected?.id === w.id}
                  onClick={() => setSearchParams({ workshop: w.id })}
                />
              ))
            ) : (
              <p className="px-2 py-8 text-center text-sm text-ink-subtle">
                No workshops match that search.
              </p>
            )}
          </div>
        </aside>

        <div className="min-w-0">
          {selected ? (
            <HostCrmPanel workshop={selected} />
          ) : (
            <div className="rounded-2xl border border-dashed border-line bg-surface p-12 text-center text-sm text-ink-subtle">
              No workshops yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WorkshopCrmButton({
  workshop,
  active,
  onClick,
}: {
  workshop: WorkshopDto;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-xl border p-3 text-left transition ${
        active ? 'border-ink bg-ink text-white' : 'border-line bg-canvas/40 hover:bg-canvas'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{workshop.title}</p>
          <p className={`mt-1 truncate text-xs ${active ? 'text-white/70' : 'text-ink-subtle'}`}>
            {formatDateTime(workshop.startsAt)}
          </p>
        </div>
        <StatusPill status={workshop.status} />
      </div>
      <p className={`mt-2 truncate text-xs ${active ? 'text-white/70' : 'text-ink-muted'}`}>
        {workshop.guest
          ? `${workshop.guest.name} · ${workshop.guest.companyName}`
          : 'No guest assigned'}
      </p>
    </button>
  );
}
